import React, { useRef, useState } from 'react'
import { Alert, StyleSheet, TouchableOpacity } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'expo-router'

import { DS } from '../../design-system'
import { useStore } from '../../models'
import { formatDate } from '@ppwr/poker'
import { MembershipTypes } from '../../models/api-store'
import ConfirmUpdate from './components/confirmUpdate'
import { BottomSheetModal } from '@gorhom/bottom-sheet'
import { useSubscriptionGuard } from '../../hooks/useSubscriptionGuard'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'

const MembershipPlansScreen: React.FC = observer(() => {
  const router = useRouter()
  const {
    game: { setToast },
    memberships: { subscription, updateUserSubscription, getMySubscription }
  } = useStore()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const isGoldMembershipEnabled = useRemoteConfig((state) => state.is_gold_membership_enabled)

  useSubscriptionGuard()

  const [loading, setLoading] = useState(false)
  const [plan, setPlan] = useState<MembershipTypes>((subscription?.type as MembershipTypes) || 'free')

  const handleUpdateSubscribe = async () => {
    if (loading) return // Prevent multiple simultaneous calls
    setLoading(true)

    try {
      await updateUserSubscription({ changeTo: plan })

      setToast({
        kind: 'success',
        message: 'Membership updated'
      })
    } catch (error) {
      console.error('Error updating subscription:', error)
      Alert.alert('Error', 'There was an error updating your subscription')
    } finally {
      setLoading(false)
      bottomSheetModalRef.current?.close()
      router.navigate('/lobby/membership')
      getMySubscription()
    }
  }

  const goToUpdateSubscription = () => {
    if (plan === 'free') {
      router.navigate('/lobby/membership-cancel-survey')
      return
    }
    return bottomSheetModalRef.current?.present()
  }

  const onPressBack = () => {
    if (router.canGoBack()) {
      router.back()
      return
    }
    router.replace('/lobby/membership')
  }

  return (
    <DS.Frame style={styles.wrapperContainer}>
      <DS.Frame style={styles.headerDivider} />
      <SafeAreaView style={styles.safeAreaContainer}>
        <DS.Frame style={styles.container}>
          <DS.Frame style={styles.headerContainer}>
            <DS.Text variant="Body Medium/Medium">Plans</DS.Text>
            <TouchableOpacity style={styles.returnButton} onPress={onPressBack}>
              <DS.Icon name="Arrow---left" size={20} color="Global/Black" />
            </TouchableOpacity>
          </DS.Frame>

          <DS.Frame style={styles.card}>
            <TouchableOpacity style={styles.checkButton} onPress={() => setPlan(MembershipTypes.free)}>
              <DS.Icon name={plan === 'free' ? 'Radio' : 'Radio-uncheck'} size={24} color="Global/Black" />
              <DS.Frame>
                <DS.Text variant="Body Medium/Medium">Free</DS.Text>
                <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
                  Lose all premium features
                </DS.Text>
              </DS.Frame>
            </TouchableOpacity>

            <DS.Frame style={styles.divider} />

            <TouchableOpacity style={styles.checkButton} onPress={() => setPlan(MembershipTypes.silver)}>
              <DS.Icon name={plan === 'silver' ? 'Radio' : 'Radio-uncheck'} size={24} color="Global/Black" />
              <DS.Frame>
                <DS.Text variant="Body Medium/Medium">Silver</DS.Text>
                <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
                  {subscription?.type === 'silver' ? 'Current' : 'Downgrade membership'}
                </DS.Text>
              </DS.Frame>
            </TouchableOpacity>

            {isGoldMembershipEnabled && (
              <TouchableOpacity style={styles.checkButton} onPress={() => setPlan(MembershipTypes.gold)}>
                <DS.Icon name={plan === 'gold' ? 'Radio' : 'Radio-uncheck'} size={24} color="Global/Black" />
                <DS.Frame>
                  <DS.Text variant="Body Medium/Medium">Gold</DS.Text>
                  <DS.Text variant="Body Small/Regular" color="Brand/Dark Grey">
                    {subscription?.type === 'gold' ? 'Current' : 'Upgrade membership'}
                  </DS.Text>
                </DS.Frame>
              </TouchableOpacity>
            )}
          </DS.Frame>

          <DS.Frame style={styles.periodContainer}>
            <DS.Text variant="Body Medium/Medium">Current Plan</DS.Text>
            <DS.Text variant="Body Medium/Regular">{`${formatDate(subscription?.startOfPeriod ?? '')} - ${formatDate(
              subscription?.endOfPeriod ?? ''
            )}`}</DS.Text>
          </DS.Frame>

          {subscription?.type !== (MembershipTypes.free as MembershipTypes) && plan === MembershipTypes.free && (
            <DS.Text variant="Body Medium/Regular">
              Downgrading today will not take affect until your current plan elapses. You will continue to get your
              premium membership benefits until then.
            </DS.Text>
          )}
          {subscription?.type === MembershipTypes.gold && plan === MembershipTypes.silver && (
            <DS.Text variant="Body Medium/Regular">
              Downgrading today will not take affect until your current plan elapses. You will continue to enjoy all the
              benefits of your Gold membership until the end of your current billing cycle. After that, your membership
              will be switched to Silver, and you'll be charged the Silver plan rate starting with your next billing
              cycle.
            </DS.Text>
          )}

          {subscription?.type !== MembershipTypes.gold && plan === MembershipTypes.gold && (
            <DS.Text variant="Body Medium/Regular">
              Upgrading today will take affect immediately. You will get the benefits of the Gold membership for the
              rest of the month and you’ll be charged Gold pricing at your next monthly renewal.
            </DS.Text>
          )}
          {subscription?.type !== plan && (
            <DS.Frame style={styles.buttonContainer}>
              <DS.Button onPress={goToUpdateSubscription} variant="Large/Primary" type="On Light">
                Continue
              </DS.Button>
            </DS.Frame>
          )}
          <ConfirmUpdate
            onSubmit={handleUpdateSubscribe}
            bottomSheetModalRef={bottomSheetModalRef}
            loading={loading}
            membershipType={subscription?.type as MembershipTypes}
          />
        </DS.Frame>
      </SafeAreaView>
    </DS.Frame>
  )
})

const styles = StyleSheet.create({
  wrapperContainer: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  safeAreaContainer: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    borderTopRightRadius: 24,
    borderTopLeftRadius: 24,
    transform: [{ translateY: -24 }]
  },
  container: {
    flex: 1,
    padding: 24,
    paddingBottom: 16,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: DS.palette['Brand/Cream'],
    position: 'relative',
    borderTopRightRadius: 24,
    borderTopLeftRadius: 24
  },
  headerContainer: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },
  card: {
    width: '100%',
    borderWidth: 1,
    borderColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    backgroundColor: DS.palette['Global/White'],
    marginTop: 24,
    padding: 16,
    justifyContent: 'space-between',
    gap: 16
  },
  checkButton: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 8
  },
  returnButton: {
    padding: 8,
    borderRadius: 16,
    position: 'absolute',
    width: 40,
    height: 40,
    top: -8,
    left: 0
  },
  stripeButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: DS.palette['Global/White'],
    padding: 16,
    width: '100%',
    borderRadius: 16,
    marginTop: 24
  },
  divider: {
    height: 1,
    backgroundColor: DS.palette['Brand/Cream'],
    width: '100%'
  },
  periodContainer: {
    marginVertical: 24
  },
  buttonContainer: {
    marginTop: 'auto',
    width: '100%'
  },
  headerDivider: {
    height: 90,
    backgroundColor: DS.palette['Global/Black'],
    width: '100%'
  }
})

export default MembershipPlansScreen
